import { Refresh } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { FramerTooltip } from "@ryde/controls";
import {
  TOOLTIP_DELAY_BUTTONS,
  type IRideInvestigationHistoryDto,
} from "@ryde/shared";

import {
  INVESTIGATION_HISTORY,
  RIDE_INVESTIGATION_E2E,
} from "../investigation.constants";
import { HistoryFeedStyled } from "../investigation.history.styled";
import type { IPlaybackModel } from "../investigation.models";
import { PanelMessageStyled } from "../investigation.panel.styled";
import { useInvestigationAuditChanges } from "../use-investigation-audit-changes";
import { useInvestigationHistoryView } from "../use-investigation-history-view";
import { InvestigationHistoryFilters } from "./investigation.history-filters";
import { InvestigationHistoryLifecycle } from "./investigation.history-lifecycle";
import { InvestigationHistoryNotices } from "./investigation.history-notices";
import { InvestigationHistoryRow } from "./investigation.history-row";

interface IInvestigationHistoryFeedProps {
  model: IPlaybackModel;
  history?: IRideInvestigationHistoryDto;
  isLoading: boolean;
  hasError: boolean;
  onRefresh: () => void;
}

/**
 * The History tab: one time-ordered list of everything that happened to the ride. Drive rows show at once
 * (they come from the investigation already loaded); the rest arrives with the lazily loaded history, which
 * the refresh button loads again.
 */
export const InvestigationHistoryFeed: FC<IInvestigationHistoryFeedProps> = ({
  model,
  history,
  isLoading,
  hasError,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const { filter, setFilter, rows, counts, lifecycle, activeRowId, selectRow } =
    useInvestigationHistoryView({ model, history });
  const auditChanges = useInvestigationAuditChanges(history);
  const key = INVESTIGATION_HISTORY.i18nKey;

  return (
    <HistoryFeedStyled>
      {lifecycle && <InvestigationHistoryLifecycle steps={lifecycle} />}
      <div className="history-toolbar">
        <InvestigationHistoryFilters
          filter={filter}
          counts={counts}
          onChange={setFilter}
        />
        <FramerTooltip
          content={t("general.REFRESH")}
          mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
        >
          {/* A disabled button fires no hover events; the span keeps the tooltip. */}
          <span>
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={isLoading}
              aria-label={t("general.REFRESH")}
              data-e2e={RIDE_INVESTIGATION_E2E.historyRefresh}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </FramerTooltip>
      </div>
      <InvestigationHistoryNotices
        history={history}
        isLoading={isLoading}
        hasError={hasError}
      />
      {rows.length > 0 && (
        <ol className="history-list">
          {rows.map(row => (
            <InvestigationHistoryRow
              key={row.id}
              row={row}
              isActive={row.id === activeRowId}
              onSelect={selectRow}
              auditChanges={
                row.entry?.audit
                  ? auditChanges.get(row.entry.audit.logId)
                  : undefined
              }
            />
          ))}
        </ol>
      )}
      {!rows.length && !isLoading && (
        <PanelMessageStyled>
          {t(filter === "all" ? `${key}.empty` : `${key}.emptyFilter`)}
        </PanelMessageStyled>
      )}
    </HistoryFeedStyled>
  );
};
